import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import { CardHeading, Notice } from '../components/ui';
import { api } from '../lib/api';
import { trackEvent } from '../lib/consent';

export default function Contact() {
  const { t } = useTranslation();

  const subjects = [
    { key: 'general', label: t('contact.subjects.general', 'General enquiry') },
    { key: 'represent', label: t('contact.subjects.represent', 'I represent a monastery') },
    { key: 'correct', label: t('contact.subjects.correct', 'Correct something you have recorded') },
    { key: 'question', label: t('contact.subjects.question', 'A question about an offering') },
  ];

  const [form, setForm] = useState({ name: '', email: '', subject: subjects[0].label, message: '' });
  const [state, setState] = useState({ status: 'idle', message: '' });

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setState({ status: 'sending', message: '' });
    // The stable enum key, never the free-text name/email/message.
    const subjectKey = subjects.find((s) => s.label === form.subject)?.key ?? 'general';
    try {
      const { message } = await api.message(form);
      setState({ status: 'done', message: message || t('contact.sentSuccess', 'Message received. We will get back to you shortly.') });
      trackEvent('generate_lead', { form: 'contact', subject: subjectKey });
      setForm({ name: '', email: '', subject: subjects[0].label, message: '' });
    } catch (error) {
      setState({ status: 'error', message: error.message || t('contact.sentError', 'Could not send message. Please email us at gompaapp@gmail.com directly.') });
      trackEvent('contact_error', { subject: subjectKey });
    }
  };

  return (
    <div className="shell shell--narrow" style={{ paddingBlock: 'var(--space-3xl) var(--space-5xl)' }}>
      <header className="stack" style={{ marginBottom: 'var(--space-2xl)' }}>
        <p className="eyebrow">{t('contact.eyebrow', 'Contact')}</p>
        <h1 className="display display--lg">{t('contact.title', 'Get in touch')}</h1>
        <p className="lede">
          {t(
            'contact.lede',
            'If you are a monastery and want to be listed, or if you have a question about an offering, write to us here.'
          )}
        </p>
      </header>

      <form className="card card__pad stack" onSubmit={submit}>
        <CardHeading icon="mail" title={t('contact.cardTitle', 'Write to us')} />

        {state.status === 'done' && <Notice tone="success" title="Received">{state.message}</Notice>}
        {state.status === 'error' && <Notice tone="danger" title="Not sent">{state.message}</Notice>}

        <div className="field">
          <label className="field__label" htmlFor="c-name">{t('contact.nameLabel', 'Your name')}</label>
          <input id="c-name" className="input" required value={form.name} onChange={set('name')} />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="c-email">{t('contact.emailLabel', 'Email')}</label>
          <input id="c-email" type="email" className="input" required value={form.email} onChange={set('email')} />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="c-subject">{t('contact.subjectLabel', 'What is this about?')}</label>
          <select id="c-subject" className="select" value={form.subject} onChange={set('subject')}>
            {subjects.map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="c-message">{t('contact.messageLabel', 'Message')}</label>
          <textarea id="c-message" className="textarea" required minLength={10} maxLength={2000} value={form.message} onChange={set('message')} />
        </div>

        <button className="btn btn--primary" disabled={state.status === 'sending'}>
          {state.status === 'sending' ? t('contact.sending', 'Sending…') : <>{t('contact.sendMessage', 'Send message')} <Icon name="arrowRight" size={17} /></>}
        </button>

        <p className="subtle">
          {t('contact.subtleNote', 'Messages are recorded and read by a person. We do not use automated replies.')}
        </p>
      </form>
    </div>
  );
}
